import Clickable from 'components/Common/Clickable'
import FormatCurrency from 'components/Common/FormatCurrency'
import InlineIcon from 'components/utils/InlineIcon'
import FormatLoyaltyPoints from 'loyalty/components/Formatters/FormatLoyaltyPoints'
import FormatLoyaltyStatusCredits from 'loyalty/components/Formatters/FormatLoyaltyStatusCredits'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import Caption from 'LuxKit/Typography/Caption'
import { rem } from 'polished'
import type { MouseEventHandler, ReactNode } from 'react'
import React, { useCallback } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

const ClickableIcon = styled(Clickable)`
  // Vertically align the icon with text
  display: inline-flex;
  vertical-align: sub;
`

const Content = styled.span`
  display: inline-flex;
  flex-wrap: wrap;
  column-gap: ${rem(4)};
  max-width: 100%;

  &.h-align-center {
    justify-content: center;
  }
`

const NoWrapChunk = styled.span`
  display: inline-flex;
  align-items: center;
  column-gap: ${rem(4)};
  white-space: nowrap;
`

interface Props {
  /**
   * @option estimate - when we don't have enough data to calculate the exact points e.g. no dates entered
   * @option exact - when we have enough data to calculate the exact points the customer will earn
   * @option additional - 'earn an additional' amount of points
   * @option nominal - we want to display that the user will earn points without showing the numerical amount
   * @option ratio - we want to display the ratio of points earned per currency unit: e.g. 1.5 points per $1
   */
  calculationType: 'estimate' | 'exact' | 'additional' | 'nominal' | 'ratio'
  earnablePoints: number
  statusCredits?: number
  pointsEarnRatio?: number
  trailingCaption?: string
  /**
   * @default start
   */
  horizontalAlign?: 'start' | 'center'
  /** does not propagate and is default-prevented */
  onInfoIconClick?: MouseEventHandler<HTMLButtonElement>
}

function hasVisibleContent(children: ReactNode) {
  return React.Children.toArray(children).some(child => (
    typeof child !== 'string' || !!child.trim()
  ))
}

function PriceRowLoyaltyPointsContent({
  calculationType,
  earnablePoints,
  statusCredits,
  pointsEarnRatio,
  trailingCaption,
  horizontalAlign = 'start',
  onInfoIconClick,
}: Props) {
  const intl = useIntl()

  const handleClick = useCallback<MouseEventHandler<HTMLButtonElement>>((event) => {
    event.preventDefault()
    event.stopPropagation()

    onInfoIconClick?.(event)
  }, [onInfoIconClick])

  const showPoints = calculationType !== 'nominal'
  const loyaltyProgramName = getLoyaltyProgramDisplayName()
  const hasStatusCredits = showPoints && !!statusCredits
  const hasCaption = !!trailingCaption

  const pointsValue = <FormatLoyaltyPoints abbreviated points={earnablePoints} />

  const statusCreditsValue = hasStatusCredits
    ? <FormatLoyaltyStatusCredits statusCredits={statusCredits} />
    : ''

  const captionText = hasCaption
    ? <Caption variant="medium" colour="light-foreground-alt" weight="normal" as="span">
        {trailingCaption}
    </Caption>
    : ''

  const infoIcon = onInfoIconClick
    ? <ClickableIcon onClick={handleClick}>
        <InlineIcon Icon={LineInfoCircleIcon} colour="normal-contrast" />
    </ClickableIcon>
    : ''

  // The message owns the visible words and separators; these renderers only
  // choose where wrapping is allowed. The plus is a primary suffix so wrapping
  // can leave "Earn {points} +" together, while status keeps the status
  // credit value and icon together.
  const renderEarn = (children: ReactNode) => (
    <NoWrapChunk key="earn">
      {children}
    </NoWrapChunk>
  )
  const renderPlus = (children: ReactNode) => {
    if (!hasStatusCredits || !hasVisibleContent(children)) return null
    return <span key="plus">{children}</span>
  }
  const renderStatus = (children: ReactNode) => {
    if (!hasStatusCredits || !hasVisibleContent(children)) return null
    return <NoWrapChunk key="status">{children}</NoWrapChunk>
  }
  const renderCaption = (children: ReactNode) => {
    if (!hasCaption || !hasVisibleContent(children)) return null
    return <NoWrapChunk key="caption">{children}</NoWrapChunk>
  }

  // Keep the whole sentence in one intl message so translators own the order of
  // points, status credits, caption and icon. The three icon slots are mutually
  // exclusive placements for the same action: the icon should sit in whichever
  // chunk is last visible, so it wraps with the final chunk of text
  const messageValues = {
    points: pointsValue,
    statusCredits: statusCreditsValue,
    captionText,
    programName: loyaltyProgramName,

    primaryInfoIcon: !hasStatusCredits && !hasCaption ? infoIcon : '',
    statusInfoIcon: hasStatusCredits && !hasCaption ? infoIcon : '',
    captionInfoIcon: hasCaption ? infoIcon : '',

    earn: renderEarn,
    // The plus is intentionally part of the primary chunk so a wrap can leave
    // "Earn {points} +" together, with status credits starting the next line.
    plus: renderPlus,
    status: renderStatus,
    caption: renderCaption,
  }

  const earningLabel = (() => {
    if (!showPoints) {
      return intl.formatMessage(
        {
          id: 'UH6PsTN_MPeA8xYm-vEc',
          defaultMessage: '<earn>Earn {programName} Points{primaryInfoIcon}</earn><caption>{captionText}{captionInfoIcon}</caption>',
          description: 'Loyalty points text when showing nominal earn message with optional caption and info icon chunks.',
        },
        messageValues,
      )
    }

    switch (calculationType) {
      case 'estimate':
        if (hasStatusCredits) {
          return intl.formatMessage(
            {
              id: '9LHRP4UH83PA1xouihcT',
              defaultMessage: '<earn>Earn from {points}{primaryInfoIcon}<plus>+</plus></earn><status>{statusCredits}{statusInfoIcon}</status><caption>{captionText}{captionInfoIcon}</caption>',
              description: 'Loyalty points text when showing estimated points with status credits, optional caption and info icon chunks.',
            },
            messageValues,
          )
        }

        return intl.formatMessage(
          {
            id: 'pRx6bUKtAAkvyg3d26mA',
            defaultMessage: '<earn>Earn from {points}{primaryInfoIcon}</earn><caption>{captionText}{captionInfoIcon}</caption>',
            description: 'Loyalty points text when showing estimated points without status credits, with optional caption and info icon chunks.',
          },
          messageValues,
        )
      case 'exact':
        if (hasStatusCredits) {
          return intl.formatMessage(
            {
              id: 'ykjsbUSG7-SDGTVPm1aw',
              defaultMessage: '<earn>Earn {points}{primaryInfoIcon}<plus>+</plus></earn><status>{statusCredits}{statusInfoIcon}</status><caption>{captionText}{captionInfoIcon}</caption>',
              description: 'Loyalty points text when showing exact points with status credits, optional caption and info icon chunks.',
            },
            messageValues,
          )
        }

        return intl.formatMessage(
          {
            id: 'n3pxoVFg9byq5fdL-7iX',
            defaultMessage: '<earn>Earn {points}{primaryInfoIcon}</earn><caption>{captionText}{captionInfoIcon}</caption>',
            description: 'Loyalty points text when showing exact points without status credits, with optional caption and info icon chunks.',
          },
          messageValues,
        )
      case 'additional':
        if (hasStatusCredits) {
          return intl.formatMessage(
            {
              id: 'gP42hWP-GR4AUCkuYOan',
              defaultMessage: '<earn>Earn an additional {points}{primaryInfoIcon}<plus>+</plus></earn><status>{statusCredits}{statusInfoIcon}</status><caption>{captionText}{captionInfoIcon}</caption>',
              description: 'Loyalty points text when showing additional points with status credits, optional caption and info icon chunks.',
            },
            messageValues,
          )
        }

        return intl.formatMessage(
          {
            id: 'vKvejzuAp5oSVbgrtFNq',
            defaultMessage: '<earn>Earn an additional {points}{primaryInfoIcon}</earn><caption>{captionText}{captionInfoIcon}</caption>',
            description: 'Loyalty points text when showing additional points without status credits, with optional caption and info icon chunks.',
          },
          messageValues,
        )
      case 'ratio':
        if (hasStatusCredits) {
          return intl.formatMessage(
            {
              id: 'LFcuWYlsFIwgbSB8pcRd',
              defaultMessage: '<earn>Earn {points} per {price}{primaryInfoIcon}<plus>+</plus></earn><status>{statusCredits}{statusInfoIcon}</status><caption>{captionText}{captionInfoIcon}</caption>',
              description: 'Loyalty points text showing points per currency unit with status credits, optional caption and info icon chunks.',
            },
            {
              ...messageValues,
              points: <FormatLoyaltyPoints abbreviated points={pointsEarnRatio ?? 0} />,
              price: <FormatCurrency value={1} />,
            },
          )
        }

        return intl.formatMessage(
          {
            id: '4X1guTG5tvxKGprAokPG',
            defaultMessage: '<earn>Earn {points} per {price}{primaryInfoIcon}</earn><caption>{captionText}{captionInfoIcon}</caption>',
            description: 'Loyalty earning rate without status credits. {points} is a formatted number with its localized abbreviated points unit already included, for example 5 pts; do not add another points noun. {price} is a formatted currency amount. Preserve the optional caption and info-icon chunks.',
          },
          {
            ...messageValues,
            points: <FormatLoyaltyPoints abbreviated points={pointsEarnRatio ?? 0} />,
            price: <FormatCurrency value={1} />,
          },
        )
    }
  })()

  return <Content className={`h-align-${horizontalAlign}`}>{earningLabel}</Content>
}

export default PriceRowLoyaltyPointsContent
